import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { recordingFolders } from '@/api/recordingFolders';
import type { FolderType, RecordingFolderRow } from '@/api/recordingFolders';
import { cn } from '@/lib/cn';
import { FolderOpen, X, ChevronDown, Plus } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';

interface Props {
  value: string | null;
  onChange: (id: string | null) => void;
  label?: string;
  disabled?: boolean;
  /** Restrict the list to folders of this type. Omit for all folder types. */
  folderType?: FolderType;
  /**
   * Customer scope for the list query. REQUIRED when the caller is a
   * super_admin / reseller_owner viewing a specific customer's content
   * — without it the backend defaults to "no filter" and returns
   * folders across every tenant the caller can see (security bug
   * 2026-05-19). office_manager / standard_user sessions can omit it;
   * RLS scopes their results to their own tenant automatically.
   */
  customerId?: string | null;
}

/**
 * Picker for a recording folder — used by folder-variant IVR nodes
 * (bulletin, series_folder, rotation_folder, random_folder,
 * latest_n_folder). When `folderType` is set, only folders of that
 * type are shown.
 */
export function FolderPicker({ value, onChange, label, disabled = false, folderType, customerId }: Props) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  function handleSearchChange(v: string) {
    setSearch(v);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setDebounced(v), 250);
  }

  const { data: selectedData } = useQuery({
    queryKey: ['recordingFolder', value],
    queryFn: () => recordingFolders.get(value!),
    enabled: !!value,
  });

  const { data: listData, isLoading } = useQuery({
    queryKey: ['recordingFolders', 'picker', folderType ?? 'all', debounced, customerId ?? ''],
    queryFn: () => recordingFolders.list({
      folder_type: folderType,
      search: debounced || undefined,
      limit: 20,
      customer_id: customerId ?? undefined,
    }),
    enabled: open,
  });

  const options: RecordingFolderRow[] = listData?.rows ?? [];

  function selectFolder(id: string | null) {
    onChange(id);
    setOpen(false);
    setSearch('');
    setDebounced('');
  }

  const displayName = value
    ? (selectedData?.name ?? 'Loading…')
    : 'None';

  return (
    <div className="relative">
      {label && (
        <p className="mb-1 text-xs font-medium text-slate-600">{label}</p>
      )}
      <button
        type="button"
        disabled={disabled}
        onClick={() => {
          setOpen((v) => !v);
          setTimeout(() => inputRef.current?.focus(), 50);
        }}
        className={cn(
          'flex h-9 w-full items-center justify-between gap-2 rounded-md border border-slate-200 bg-white px-3 text-sm transition-colors',
          'hover:border-slate-300 focus:outline-none focus:ring-2 focus:ring-slate-950 focus:ring-offset-2',
          'disabled:cursor-not-allowed disabled:opacity-50',
          open && 'border-slate-400',
        )}
      >
        <span className="flex items-center gap-2 truncate">
          <FolderOpen className="h-3.5 w-3.5 shrink-0 text-slate-400" />
          <span className={cn('truncate', !value && 'text-slate-400')}>
            {displayName}
          </span>
        </span>
        <span className="flex items-center gap-1 shrink-0">
          {value && (
            <span
              role="button"
              aria-label="Clear folder"
              tabIndex={0}
              onClick={(e) => { e.stopPropagation(); onChange(null); }}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); onChange(null); } }}
              className="rounded p-0.5 text-slate-400 hover:text-slate-600"
            >
              <X className="h-3 w-3" />
            </span>
          )}
          <ChevronDown className="h-3.5 w-3.5 text-slate-400" />
        </span>
      </button>

      {open && (
        <div className="absolute z-50 mt-1 w-full rounded-md border border-slate-200 bg-white shadow-lg">
          <div className="border-b border-slate-100 p-2">
            <input
              ref={inputRef}
              value={search}
              onChange={(e) => handleSearchChange(e.target.value)}
              placeholder={`Search ${folderType ?? 'folders'}…`}
              className="w-full rounded border border-slate-200 px-2 py-1.5 text-xs outline-none focus:border-slate-400"
            />
          </div>
          <div className="max-h-48 overflow-y-auto">
            <button
              type="button"
              onClick={() => selectFolder(null)}
              className="flex w-full items-center gap-2 px-3 py-2 text-xs text-slate-500 hover:bg-slate-50 border-b border-slate-100"
            >
              <X className="h-3 w-3" /> None
            </button>

            {isLoading ? (
              <div className="px-3 py-4 text-center text-xs text-slate-400">Loading…</div>
            ) : options.length === 0 ? (
              <div className="px-3 py-4 text-center text-xs text-slate-400">
                {debounced ? `No results for "${debounced}"` : 'No folders found.'}
              </div>
            ) : (
              options.map((f) => (
                <button
                  key={f.id}
                  type="button"
                  onClick={() => selectFolder(f.id)}
                  className={cn(
                    'flex w-full items-center gap-2 px-3 py-2 text-left text-xs hover:bg-slate-50',
                    value === f.id && 'bg-blue-50 text-blue-700',
                  )}
                >
                  <FolderOpen className="h-3 w-3 shrink-0 text-slate-300" />
                  <span className="flex-1 truncate font-medium">{f.name}</span>
                  {f.folder_type && (
                    <span className="shrink-0 rounded bg-slate-100 px-1 py-0.5 text-[9px] font-medium uppercase text-slate-500">
                      {f.folder_type}
                    </span>
                  )}
                </button>
              ))
            )}
          </div>
          {/* Inline "+ Create new folder" — opens a tiny dialog. Visible
              only when we have a customer scope (super_admin / reseller_owner
              without an entered customer have no target for the new
              folder, so we hide the action rather than 400 on submit). */}
          {customerId && (
            <button
              type="button"
              onClick={() => { setOpen(false); setCreateOpen(true); }}
              className="flex w-full items-center gap-2 border-t border-slate-100 px-3 py-2 text-xs font-medium text-blue-600 hover:bg-blue-50"
            >
              <Plus className="h-3 w-3" /> Create new folder…
            </button>
          )}
        </div>
      )}
      {createOpen && customerId && (
        <InlineCreateFolderDialog
          customerId={customerId}
          folderType={folderType}
          onCreated={(row) => {
            // Select the newly-created folder so the user doesn't have to
            // find it in the picker. Closing the dialog is on the dialog
            // itself.
            onChange(row.id);
            setCreateOpen(false);
          }}
          onClose={() => setCreateOpen(false)}
        />
      )}
    </div>
  );
}

/**
 * Tiny inline create-folder dialog, embedded in the picker so users
 * don't have to navigate away to the /folders page to add a folder
 * mid-form. Uses the existing recordingFolders.create API. folder_type
 * is passed through when the parent picker is restricted to a specific
 * type (so the new folder is usable in the picker that just spawned
 * it); otherwise the folder is typeless.
 */
function InlineCreateFolderDialog({
  customerId, folderType, onCreated, onClose,
}: {
  customerId: string;
  folderType?: FolderType;
  onCreated: (row: RecordingFolderRow) => void;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () => recordingFolders.create({
      name: name.trim(),
      ...(folderType ? { folder_type: folderType } : {}),
      ...(description.trim() ? { description: description.trim() } : {}),
      customer_id: customerId,
    }),
    onSuccess: (row) => {
      qc.invalidateQueries({ queryKey: ['recordingFolders'] });
      onCreated(row);
    },
    onError: (err) => setError(err instanceof Error ? err.message : 'Create failed.'),
  });

  const canSubmit = name.trim().length > 0 && !create.isPending;

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent
        className="max-w-md"
        // Stop submit / clicks bubbling up to any parent <form> the
        // picker was mounted inside (e.g. the IVR node editor). Without
        // this, submitting the dialog also triggers the parent form's
        // onSubmit — same bug class as the upload dialog (CLAUDE.md
        // rule 18).
        onClick={(e) => e.stopPropagation()}
      >
        <DialogHeader>
          <DialogTitle>New folder</DialogTitle>
        </DialogHeader>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (canSubmit) create.mutate();
          }}
          className="space-y-4 py-2"
        >
          <div className="space-y-1">
            <Label htmlFor="fldp-name">Name</Label>
            <input
              id="fldp-name"
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Weekly announcements"
              className="block w-full rounded-md border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-400"
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="fldp-desc">Description (optional)</Label>
            <textarea
              id="fldp-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              maxLength={1000}
              placeholder="What goes in this folder…"
              className="block w-full resize-y rounded-md border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-400"
            />
          </div>
          {error && (
            <p role="alert" className="text-sm text-destructive">{error}</p>
          )}
          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="outline" disabled={create.isPending}>
                Cancel
              </Button>
            </DialogClose>
            <Button type="submit" disabled={!canSubmit}>
              {create.isPending ? 'Creating…' : 'Create folder'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
